import { createContext, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import axios from 'api/axiosInstance';
import endpoints from 'api/endpoints';
import { useSnackbar } from './SnackbarContext';

// ==============================|| COMPANY CONTEXT ||============================== //

const CompanyContext = createContext();

// ==============================|| COMPANY PROVIDER ||============================== //

export function CompanyProvider({ children }) {
  const { showSnackbar } = useSnackbar();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState([]);
  const fetchCompanies = async () => {
    try {
      setLoading(true);

      // Get token from localStorage
      const token = localStorage.getItem('token');

      // Make API call with token using companyList endpoint
      const response = await axios.get(endpoints.company.companyList(), {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      console.log('Companies fetched:', response.data);

      // Handle different response structures
      let companiesData = [];
      if (Array.isArray(response.data)) {
        companiesData = response.data;
      } else if (response.data.results && Array.isArray(response.data.results)) {
        companiesData = response.data.results;
      } else if (response.data.data && Array.isArray(response.data.data)) {
        companiesData = response.data.data;
      }

      setCompanies(companiesData);
      return companiesData;
    } catch (error) {
      console.error('Error fetching companies:', error);
      showSnackbar(error.response?.data?.message || error.message || 'Failed to fetch companies', 'error');
      return [];
    } finally {
      setLoading(false);
    }
  };

  const updateCompany = async (id, companyData) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const response = await axios.patch(endpoints.company.updateCompany(id), companyData, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      showSnackbar('Company updated successfully', 'success');
      // Refresh the companies list
      await fetchCompanies();
      return response.data;
    } catch (error) {
      console.error('Error updating company:', error);
      showSnackbar(error.response?.data?.message || error.message || 'Failed to update company', 'error');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const deleteCompany = async (id) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      await axios.delete(endpoints.company.deleteCompany(id), {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      showSnackbar('Company deleted successfully', 'success');
      // Refresh the companies list
      await fetchCompanies();
    } catch (error) {
      console.error('Error deleting company:', error);
      showSnackbar(error.response?.data?.message || error.message || 'Failed to delete company', 'error');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return (
    <CompanyContext.Provider value={{ companies, loading, fetchCompanies, updateCompany, deleteCompany, list, setList }}>
      {children}
    </CompanyContext.Provider>
  );
}

CompanyProvider.propTypes = {
  children: PropTypes.node
};

// ==============================|| COMPANY HOOK ||============================== //

export const useCompany = () => {
  const context = useContext(CompanyContext);
  if (!context) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
};
